/**
 * Structural (token-grammar) validator for date/time format strings — a framework-agnostic
 * mirror of the CoreConnect API's `DateFnsFormatValidator` (CoreConnect.Application.Common).
 * Accepts any combination of the supported date-fns tokens (a deliberate subset — see the token
 * sets below), separators and quoted literals (so users can define custom formats), while unknown
 * tokens — incl. .NET-style DD/YYYY/tt, which date-fns either throws on or silently renders as
 * garbage — are rejected with a targeted hint.
 *
 * PARITY CONTRACT: the canonical test vectors in `__tests__/formatValidation.test.ts` are
 * duplicated verbatim in the API's `DateFnsFormatValidatorTests.cs`. Error message strings are
 * part of the contract — change them in lockstep across both repos.
 */

export interface FormatValidationResult {
  valid: boolean;
  error?: string;
}

const SEPARATOR_CHARS = " ,./-:()";

const DAY_TOKENS = new Set(["d", "dd", "do"]);
const WEEKDAY_TOKENS = new Set(["EEE", "EEEE"]);
const MONTH_TOKENS = new Set(["M", "MM", "MMM", "MMMM"]);
const YEAR_TOKENS = new Set(["yy", "yyyy"]);
const HOUR12_TOKENS = new Set(["h", "hh"]);
const HOUR24_TOKENS = new Set(["H", "HH"]);
const MINUTE_TOKENS = new Set(["m", "mm"]);
const SECOND_TOKENS = new Set(["s", "ss"]);
const MERIDIEM_TOKEN = "a";

/** Common wrong-token → right-token hints for DATE formats (.NET / minute-vs-month mixups). */
const DATE_HINTS: Record<string, string> = {
  D: "d",
  DD: "dd",
  Y: "yyyy",
  YY: "yy",
  YYYY: "yyyy",
  E: "EEE",
  EE: "EEE",
  m: "M",
  mm: "MM",
};

/** Common wrong-token → right-token hints for TIME formats (.NET meridiem / month-vs-minute mixups). */
const TIME_HINTS: Record<string, string> = {
  t: "a",
  tt: "a",
  A: "a",
  M: "m",
  MM: "mm",
};

const invalid = (error: string): FormatValidationResult => ({ valid: false, error });

const isLetter = (c: string): boolean => /[a-zA-Z]/.test(c);

/**
 * Walks the format string collecting letter-run tokens. Quoted literals ('...', with '' as the
 * escaped apostrophe) and separator characters are skipped; any other character, or a letter run
 * not in `isKnownToken`, fails with the canonical error (with a hint when the mixup is known).
 */
function tokenize(
  value: string,
  isKnownToken: (token: string) => boolean,
  hints: Record<string, string>,
): { result: FormatValidationResult; tokens: string[] } {
  const tokens: string[] = [];
  let i = 0;

  while (i < value.length) {
    const c = value[i];

    if (c === "'") {
      i++;
      let closed = false;
      while (i < value.length) {
        if (value[i] === "'") {
          if (i + 1 < value.length && value[i + 1] === "'") {
            i += 2; // '' inside a literal = escaped apostrophe
            continue;
          }
          closed = true;
          i++;
          break;
        }
        i++;
      }
      if (!closed) return { result: invalid("Unterminated quoted literal."), tokens };
      continue;
    }

    if (isLetter(c)) {
      const start = i;
      while (i < value.length && value[i] === c) i++;
      let run = value.slice(start, i);

      // date-fns ordinal day-of-month is the two-letter token 'do' — the only
      // mixed-letter token the grammar supports.
      if (run === "d" && value[i] === "o") {
        run = "do";
        i++;
      }

      if (!isKnownToken(run)) {
        const hint = hints[run];
        return {
          result: invalid(hint ? `Unknown token '${run}' — use '${hint}' instead.` : `Unknown token '${run}'.`),
          tokens,
        };
      }

      tokens.push(run);
      continue;
    }

    if (SEPARATOR_CHARS.includes(c)) {
      i++;
      continue;
    }

    return { result: invalid(`Unsupported character '${c}'.`), tokens };
  }

  return { result: { valid: true }, tokens };
}

/**
 * Validates a DATE format string: only supported date-fns date tokens, separators and quoted
 * literals; must contain a day, a month and a year token. Nullish or empty input is valid
 * (means "unset / use the caller's default"), mirroring the API's `string?` overload — a
 * non-empty whitespace-only string is NOT treated as empty and fails the day-token check, matching
 * the server's `string.IsNullOrEmpty` semantics.
 */
export function validateDateFormat(value: string | null | undefined): FormatValidationResult {
  if (!value) return { valid: true };

  const { result, tokens } = tokenize(
    value,
    (t) => DAY_TOKENS.has(t) || WEEKDAY_TOKENS.has(t) || MONTH_TOKENS.has(t) || YEAR_TOKENS.has(t),
    DATE_HINTS,
  );
  if (!result.valid) return result;

  if (!tokens.some((t) => DAY_TOKENS.has(t))) return invalid("Date format must include a day token (d, dd or do).");
  if (!tokens.some((t) => MONTH_TOKENS.has(t))) {
    return invalid("Date format must include a month token (M, MM, MMM or MMMM).");
  }
  if (!tokens.some((t) => YEAR_TOKENS.has(t))) return invalid("Date format must include a year token (yy or yyyy).");

  return { valid: true };
}

/**
 * Validates a TIME format string: only supported date-fns time tokens, separators and quoted
 * literals; must contain an hour and a minute token; 12-hour tokens require the meridiem token
 * 'a' (and vice versa) so an ambiguous 12h-without-AM/PM can never be stored. Nullish or empty
 * input is valid (means "unset / use the caller's default"), mirroring the API's `string?`
 * overload; a non-empty whitespace-only string is NOT treated as empty and fails the hour-token check.
 */
export function validateTimeFormat(value: string | null | undefined): FormatValidationResult {
  if (!value) return { valid: true };

  const { result, tokens } = tokenize(
    value,
    (t) =>
      HOUR12_TOKENS.has(t) ||
      HOUR24_TOKENS.has(t) ||
      MINUTE_TOKENS.has(t) ||
      SECOND_TOKENS.has(t) ||
      t === MERIDIEM_TOKEN,
    TIME_HINTS,
  );
  if (!result.valid) return result;

  const hasHour12 = tokens.some((t) => HOUR12_TOKENS.has(t));
  const hasHour24 = tokens.some((t) => HOUR24_TOKENS.has(t));

  if (!hasHour12 && !hasHour24) return invalid("Time format must include an hour token (h, hh, H or HH).");
  if (!tokens.some((t) => MINUTE_TOKENS.has(t))) return invalid("Time format must include a minute token (m or mm).");

  const hasMeridiem = tokens.includes(MERIDIEM_TOKEN);
  if (hasHour12 && !hasMeridiem) return invalid("12-hour time format requires the meridiem token 'a'.");
  if (hasMeridiem && !hasHour12) return invalid("Meridiem token 'a' requires a 12-hour hour token (h or hh).");

  return { valid: true };
}
